import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type NativePrintProps, NativeReportShell, useReportFilters } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';
import { nativePrintSession, useNativePrintMode } from '~/nativeLenses/nativePrintSession';

import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, MenuItem, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { CartesianGrid, Line, LineChart as RechartsLineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import { csvFileName } from '../csvValues';
import { download } from '../RmMonitor/liveModel';
import {
  OPTIONS_CARD_IDS,
  OPTION_LIMIT,
  REPORT_CARD_IDS,
  SELECTION_FILTERS,
  type SelectionId,
  type Selections,
  activeSelectionCount,
  optionsRequest,
  readSelections,
  reportRequest,
  writeSelections,
} from './filterSelections';
import { accommodationPageContext } from './pageContext';
import { TacticalWindows, TACTICAL_WINDOWS, tacticalWindows, tacticalWindowProblem, clearTacticalWindows } from './tacticalWindows';
import { CHOICES, DATE_KEYS, type DateKey, SPLITS, clearFilters, dateProblem, readFilters, readView, writeFilters, writeView } from './urlState';

import {
  CARDS,
  DEFAULT_FILTERS,
  PAGE_ID,
  VIEWS,
  cardRows,
  conversionCsv,
  conversionDays,
  conversionKpis,
  conversionModel,
  formatInteger,
  formatMoney,
  formatPercent,
  formatSignedPercent,
  lpcCsv,
  lpcModel,
  previousWindow,
  ratio,
  supplierCsv,
  supplierModel,
  tacticalCsv,
  tacticalColumns,
  tacticalDisplayRow,
  tacticalGrandTotal,
  tacticalMarginColour,
  tacticalRows,
  tacticalTotals,
  type DayPoint,
  type LensFilters,
  type ViewId,
} from './liveDataAdapter';


function Content() {
  // The worksheet, dates, settings and Tableau's quick filters all live in the URL, so a copied link
  // opens the same view and the same numbers.
  const [params, setParams] = useSearchParams();
  const view = readView(params);
  const filters = useMemo(() => readFilters(params), [params]);
  const setView = (next: ViewId) => setParams((current) => writeView(next, current), { replace: true });
  const setFilters = (update: (current: LensFilters) => LensFilters) =>
    setParams((current) => writeFilters(update(readFilters(current)), current), { replace: true });
  // A typed date that is not usable stays in the field with a message and is not sent.
  const [dateDrafts, setDateDrafts] = useState<Partial<Record<DateKey, string>>>({});
  const selections = useMemo(() => readSelections(params), [params]);
  const setSelections = (next: Selections) => setParams((current) => writeSelections(next, current), { replace: true });
  const windowProblem = view === VIEWS[3] ? tacticalWindowProblem(params) : null;
  const request = useMemo(() => ({ ...reportRequest(filters, selections), ...tacticalWindows(params) }), [filters, selections, params]);
  // Only the worksheet cards; the filter-value cards exist for the dropdown lists and return raw rows.
  useReportFilters(request);
  const query = useGetReportingLeLensData(windowProblem ? undefined : nativeLensId(PAGE_ID), request, false, undefined, undefined, undefined, REPORT_CARD_IDS);
  // Dropdown values load once per reporting window, separately from the numbers.
  const listRequest = useMemo(() => optionsRequest(filters), [filters]);
  const optionsQuery = useGetReportingLeLensData(nativeLensId(PAGE_ID), listRequest, true, undefined, undefined, undefined, OPTIONS_CARD_IDS);
  const cards = query.data?.cards;
  const conversion = useMemo(() => conversionModel(cardRows(cards, CARDS.conversion)), [cards]);
  const supplier = useMemo(() => supplierModel(cardRows(cards, CARDS.supplier)), [cards]);
  const lpc = useMemo(() => lpcModel(cardRows(cards, CARDS.lpc)), [cards]);
  const tactical = useMemo(() => tacticalRows(cardRows(cards, CARDS.tactical)), [cards]);
  const activeCard = view === VIEWS[0] ? CARDS.conversion : view === VIEWS[1] ? CARDS.supplier : view === VIEWS[2] ? CARDS.lpc : CARDS.tactical;
  const active = cards?.find((card) => card.cardId === activeCard);
  const ready = !windowProblem && !query.isFetching && !query.isError && !query.isShowingPreviousData
    && (active?.status === 'ready' || active?.status === 'empty');
  const pageContext = accommodationPageContext(view, filters, selections);
  const windowFilters = view === VIEWS[3]
    ? Object.fromEntries(Object.entries(tacticalWindows(params)).flatMap(([key, range]) =>
        Object.entries(range).map(([bound, value]) => [`${key}_${bound}`, value])))
    : {};
  const selectionFields = SELECTION_FILTERS[view];
  const listedOptions = optionsQuery.data?.filterOptions ?? {};
  const selectionValues = (id: SelectionId) => id === 'tac_prior_complete' || id === 'tac_spoofed' || id === 'lpc_had_tactical' ? ['true', 'false'] : listedOptions[id] ?? [];
  const selectionSchema: Record<string, PageField> = Object.fromEntries(selectionFields.map(({ id }) => {
    const choices = selectionValues(id).filter((value) => value && !value.includes(','));
    return [id, choices.length <= 100 && selectionValues(id).length < OPTION_LIMIT
      ? { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...choices] }
      : { type: 'text', multiple: true, maxSelections: 200, maxLength: 4000 }];
  }));
  const bridgeSchema = {
    ...selectionSchema,
    ...pageContext.filterSchema,
    ...Object.fromEntries(Object.keys(windowFilters).map((key) => [key, { type: 'date' as const }])),
  };
  usePageBridge({
    pageId: PAGE_ID,
    view: {
      ...pageContext,
      filters: { ...pageContext.filters, ...windowFilters, ...Object.fromEntries(selectionFields.map(({ id }) => [id, (selections[id] ?? (id === 'tac_prior_complete' || id === 'lpc_had_tactical' ? ['true'] : [])).join(',')])) },
      filterSchema: bridgeSchema,
      ...(view === VIEWS[3] ? { campaignWindows: tacticalWindows(params) } : {}),
      ready,
      dataMode: 'BigQuery through Lens for the selected reporting scope',
      selectionLabels: Object.fromEntries(selectionFields.map(({ id, label }) => [id, label])),
      selectionInstructions: 'Quick filters accept comma-separated listed values; empty clears the selection. Values containing commas cannot be edited here. For prior window complete, select both true and false to include all.',
      summary: {
        worksheet: view,
        rows: view === VIEWS[0] ? conversion.table.length : view === VIEWS[1] ? supplier.table.length : view === VIEWS[2] ? lpc.table.length : tactical.detail.length,
        headlines: ready ? view === VIEWS[0] ? {
          'Total Package Option Impressions': formatInteger(conversion.totals.current.impressions),
          Packages: formatInteger(conversion.totals.current.packages),
          'CR (Packages/Page Views)': formatPercent(conversionKpis(conversion.totals).cr.current),
          MpPOI: formatMoney(conversionKpis(conversion.totals).mppoi.current ?? 0, 2),
          'GM (BR)': formatMoney(conversion.totals.current.margin, 0),
          'TTV (BR)': formatMoney(conversion.totals.current.ttv, 0),
        } : view === VIEWS[1] ? {
          'LPC Impression Share': formatPercent(supplier.shares.lpc), 'LPC Lead Rate': formatPercent(supplier.shares.lpcLead),
          'LPP Impression Share': formatPercent(supplier.shares.lpp), 'LPP Lead Rate': formatPercent(supplier.shares.lppLead),
        } : view === VIEWS[2] ? {
          'LPC Rate Impressions': formatInteger(lpc.current.impressions), 'Page Views': formatInteger(lpc.current.pageViews),
          Packages: formatInteger(lpc.current.packages), 'CR (Packages/Page Views)': formatPercent(lpc.current.cr),
          'LPC Impression Share': formatPercent(lpc.current.share, 1), 'CR (Packages/Impressions)': formatPercent(lpc.current.impressionCr),
          'GM AR': formatMoney(lpc.current.margin, 0),
        } : {
          'TTV During': formatMoney(tacticalTotals(tactical.kpis).ttvDuring, 0), 'TTV Prior': formatMoney(tacticalTotals(tactical.kpis).ttvPrior, 0),
          'Items During': formatInteger(tacticalTotals(tactical.kpis).itemsDuring), 'Items Prior': formatInteger(tacticalTotals(tactical.kpis).itemsPrior),
          'Margin During': formatMoney(tacticalTotals(tactical.kpis).marginDuring, 0), 'Margin Prior': formatMoney(tacticalTotals(tactical.kpis).marginPrior, 0),
        } : undefined,
      },
    },
    edit: (changes) => {
      if (!ready) throw new Error('Page data is still loading.');
      const targetView = VIEWS.includes(changes.view as ViewId) ? changes.view as ViewId : view;
      const targetFilters = { ...filters, ...(Object.fromEntries(Object.entries(changes).filter(([, value]) => typeof value === 'string'))) };
      validatePageFilters(changes, bridgeSchema);
      for (const [key, choices] of Object.entries(SPLITS)) {
        if (typeof changes[key] === 'string' && !(choices as ReadonlyArray<string>).includes(changes[key] as string)) throw new Error(`Invalid ${key}: choose a listed split.`);
      }
      const nextSelections = { ...selections };
      for (const { id } of selectionFields) {
        if (typeof changes[id] !== 'string') continue;
        if (optionsQuery.isFetching || optionsQuery.isError) throw new Error('Filter values are not ready.');
        const values = (changes[id] as string).split(',').map((value) => value.trim()).filter(Boolean);
        if ((id === 'tac_prior_complete' || id === 'lpc_had_tactical') && !values.length) throw new Error('Select both true and false to include all values.');
        if (values.some((value) => !selectionValues(id).includes(value))) throw new Error(`Invalid ${id}: choose listed values without commas.`);
        nextSelections[id] = values;
      }
      const today = sydneyToday();
      DATE_KEYS.forEach((key) => {
        if (typeof changes[key] !== 'string') return;
        const problem = dateProblem(key, changes[key] as string, { ...filters, ...targetFilters }, today);
        if (problem) throw new Error(problem);
      });
      const nextFilters = { ...filters };
      (Object.keys(DEFAULT_FILTERS) as Array<keyof LensFilters>).forEach((key) => {
        if (typeof changes[key] === 'string') nextFilters[key] = changes[key];
      });
      const next = writeSelections(nextSelections, writeView(targetView, writeFilters(nextFilters, params)));
      for (const { id } of TACTICAL_WINDOWS) {
        for (const bound of ['from', 'to']) {
          const key = `${id}_${bound}`;
          if (typeof changes[key] === 'string') next.set(key, changes[key]);
        }
      }
      if (targetView === VIEWS[3]) {
        const problem = tacticalWindowProblem(next);
        if (problem) throw new Error(problem);
      }
      setParams(next, { replace: true });
    },
  });
  const set = (key: keyof LensFilters, value: string) => {
    if ((DATE_KEYS as ReadonlyArray<string>).includes(key)) {
      const dateKey = key as DateKey;
      if (dateProblem(dateKey, value, filters, sydneyToday())) {
        setDateDrafts((current) => ({ ...current, [dateKey]: value }));
        return;
      }
      setDateDrafts((current) => {
        const next = { ...current };
        delete next[dateKey];
        return next;
      });
    }
    setFilters((current) => ({ ...current, [key]: value }));
  };
  // One reset for the whole page: dates, settings and every worksheet's quick filters.
  const resetAll = () => {
    setDateDrafts({});
    setParams((current) => writeSelections({}, clearTacticalWindows(clearFilters(current))), { replace: true });
  };
  const changedFromDefault =
    (Object.keys(DEFAULT_FILTERS) as Array<keyof LensFilters>).some((key) => filters[key] !== DEFAULT_FILTERS[key]) ||
    Object.values(selections).some((values) => values?.length) ||
    Object.keys(dateDrafts).length > 0 ||
    params.toString() !== clearTacticalWindows(params).toString();
  const period = view === VIEWS[3] ? undefined : { from: filters.cp_start, to: filters.cp_end };
  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: 'background.default', minHeight: '100%', minWidth: 0, fontFamily: FONT_STACK_SUISSE_INTL, '& .MuiTypography-root, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiButton-root, & .MuiTab-root': { fontFamily: 'inherit' } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
        <Typography component="h1" variant="h5" sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: { xs: 22, sm: 24 }, fontWeight: 600, lineHeight: 1.25 }}>Accommodation Views, Impressions and Sales</Typography>
        {view === VIEWS[3] ? <Typography variant="body2" color="text.secondary">Historical tactical campaigns use their own campaign and prior windows, shown in the table. Current and previous period filters apply to the other tabs.</Typography> : <Typography variant="body2" color="text.secondary">
          {pageContext.dataScope} {view !== VIEWS[2] ? `Margin max ${filters.margin_max.toLowerCase()}d.` : ''}
        </Typography>}
        {view !== VIEWS[3] ? <Typography variant="caption" color="text.secondary">Choose the reporting dates below for a custom period. Select Custom comparison to edit its dates.</Typography> : null}
        </Box>
        <PageChatButton />
      </Box>
      <Tabs value={view} onChange={(_, next: ViewId) => setView(next)} variant="scrollable">
        {VIEWS.map((item) => <Tab key={item} value={item} label={<ViewLabel name={item} />} />)}
      </Tabs>
      {view !== VIEWS[3] ? <FilterBar view={view} filters={filters} drafts={dateDrafts} onChange={set} /> : null}
      <SelectionBar
        view={view}
        selections={selections}
        options={optionsQuery.data?.filterOptions ?? {}}
        loading={optionsQuery.isLoading}
        failed={optionsQuery.isError}
        onChange={setSelections}
      />
      {changedFromDefault ? (
        <Box>
          <Button variant="text" size="small" onClick={resetAll}>
            Reset all to default
          </Button>
        </Box>
      ) : null}
      {view === VIEWS[3] ? <TacticalWindows params={params} onChange={(next) => setParams(next, { replace: true })} /> : null}
      {windowProblem ? <Alert severity="warning">{windowProblem}</Alert> : null}
      {!windowProblem && !ready && active?.status !== 'error' && !query.isError ? <CircularProgress /> : null}
      {query.isError || active?.status === 'error' ? (
        <Alert
          severity="error"
          data-error={active?.message || String(query.error)}
          action={
            <Button color="inherit" size="small" disabled={query.isFetching} onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          The numbers for this worksheet could not be loaded. Nothing was changed, so try again.
        </Alert>
      ) : null}
      {ready && view === VIEWS[0] ? <ConversionView model={conversion} csvText={conversionCsv(conversion.table.filter((row) => row.period === 'Current Period'))} both={filters.conv_scope === 'CP and PP'} fileName={csvFileName('spp-conversion', period?.from, period?.to)} onReset={resetAll} /> : null}
      {ready && view === VIEWS[1] ? <SupplierView model={supplier} csvText={supplierCsv(supplier.table)} fileName={csvFileName('spp-supplier', period?.from, period?.to)} onReset={resetAll} /> : null}
      {ready && view === VIEWS[2] ? <LpcView model={lpc} csvText={lpcCsv(lpc.table)} fileName={csvFileName('lpc-performance', period?.from, period?.to)} onReset={resetAll} /> : null}
      {!windowProblem && ready && view === VIEWS[3] ? <TacticalView model={tactical} csvText={tacticalCsv(tactical.detail)} fileName={csvFileName('lpc-tactical')} onReset={resetAll} /> : null}
    </Box>
  );
}

function FilterBar({ view, filters, drafts, onChange }: { view: ViewId; filters: LensFilters; drafts: Partial<Record<DateKey, string>>; onChange: (key: keyof LensFilters, value: string) => void }) {
  const splits: Array<keyof typeof SPLITS> = view === VIEWS[1] ? ['sup_split1', 'sup_split2'] : view === VIEWS[2] ? ['lpc_split1', 'lpc_split2'] : view === VIEWS[0] ? ['conv_split1', 'conv_split2'] : [];
  const prior = previousWindow(filters);
  const automaticComparison = filters.comparison === 'Last Year';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <DateField label="Reporting start" value={drafts.cp_start ?? filters.cp_start} error={dateError('cp_start', drafts, filters)} onChange={(value) => onChange('cp_start', value)} />
      <DateField label="Reporting end" value={drafts.cp_end ?? filters.cp_end} error={dateError('cp_end', drafts, filters)} onChange={(value) => onChange('cp_end', value)} />
      <DateField label="Previous start" value={automaticComparison ? prior.from : drafts.pp_start ?? prior.from} error={automaticComparison ? null : dateError('pp_start', drafts, filters)} disabled={automaticComparison} onChange={(value) => onChange('pp_start', value)} />
      <DateField label="Previous end" value={automaticComparison ? prior.to : drafts.pp_end ?? prior.to} error={automaticComparison ? null : dateError('pp_end', drafts, filters)} disabled={automaticComparison} onChange={(value) => onChange('pp_end', value)} />
      <Choice label="Comparison logic" value={filters.comparison} options={CHOICES.comparison} onChange={(value) => onChange('comparison', value)} />
      <Choice label="Table CP/PP" value={filters.conv_scope} options={CHOICES.conv_scope} onChange={(value) => onChange('conv_scope', value)} />
      {view !== VIEWS[2] ? <Choice label="Margin Max" value={filters.margin_max} options={CHOICES.margin_max} onChange={(value) => onChange('margin_max', value)} /> : null}
      {view === VIEWS[0] ? <Choice label="Rate Position" value={filters.rate_position} options={CHOICES.rate_position} onChange={(value) => onChange('rate_position', value)} /> : null}
      {splits.map((key) => (
        <Choice
          key={key}
          label={key.startsWith('conv') ? (key.endsWith('1') ? 'Data Split | Offer Views' : 'Data Split 2 | Offer Views') : key.startsWith('sup') ? (key.endsWith('1') ? 'Data Split | Suppliers' : 'Data Split 2 | Suppliers') : key.endsWith('1') ? 'Data Split 1 | LPC' : 'Data Split 2 | LPC'}
          value={filters[key]}
          options={SPLITS[key]}
          onChange={(value) => onChange(key, value)}
        />
      ))}
    </Box>
  );
}

function SelectionBar({ view, selections, options, loading, failed, onChange }: { view: ViewId; selections: Selections; options: Record<string, Array<string>>; loading: boolean; failed: boolean; onChange: (next: Selections) => void }) {
  const active = activeSelectionCount(view, selections);
  const select = (id: SelectionId, values: Array<string>) => onChange({ ...selections, [id]: id === 'lpc_had_tactical' && !values.length ? ['true', 'false'] : values });
  const clear = () => onChange(Object.fromEntries(Object.entries(selections).filter(([id]) => !SELECTION_FILTERS[view].some((filter) => filter.id === id))));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: view === VIEWS[3] ? { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } : 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
        {SELECTION_FILTERS[view].map(({ id, label }) => {
          const selected = selections[id] ?? (id === 'tac_prior_complete' || id === 'lpc_had_tactical' ? ['true'] : []);
          const listed = id === 'tac_prior_complete' || id === 'tac_spoofed' || id === 'lpc_had_tactical' ? ['true', 'false'] : options[id] ?? [];
          // Lens lists at most OPTION_LIMIT values per control, so a long list also accepts a typed value.
          const capped = listed.length >= OPTION_LIMIT;
          const choices = [...new Set([...listed.filter(Boolean), ...selected])];
          return (
            <Autocomplete
              key={id}
              multiple
              freeSolo={capped}
              disableCloseOnSelect
              size="small"
              limitTags={1}
              loading={loading}
              options={choices}
              value={selected}
              onChange={(_, values) => select(id, values.map(String).filter(Boolean))}
              renderTags={(values, getTagProps) => values.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} {...tagProps} label={option} size="small" />;
              })}
              renderOption={({ key: _key, ...optionProps }, option, { selected: checked }) => (
                <Box component="li" {...optionProps} key={option}>
                  <Checkbox checked={checked} size="small" sx={{ mr: 0.5 }} />
                  {option}
                </Box>
              )}
              renderInput={(inputProps) => (
                <TextField
                  {...inputProps}
                  label={label}
                  placeholder={selected.length ? undefined : 'All'}
                  helperText={id === 'tac_prior_complete' ? 'Defaults to complete windows. Select both values for all.' : id === 'lpc_had_tactical' ? 'Defaults to true. Select both values for all.' : capped ? `First ${OPTION_LIMIT} values. Type an exact value and press Enter to add another.` : undefined}
                />
              )}
              sx={{ minWidth: 0, '& .MuiAutocomplete-tag': { maxWidth: 'calc(100% - 45px)' } }}
            />
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {failed ? 'Filter values could not be loaded. Selections in the link still apply.' : active ? `${active} filter${active === 1 ? '' : 's'} applied on this worksheet.` : view === VIEWS[3] ? 'Default scope: complete prior windows; other categorical filters include all values.' : view === VIEWS[2] ? 'Default scope: vendors with a tactical campaign; other categorical filters include all values.' : 'All values. Choose values above to narrow this worksheet.'}
        </Typography>
        {active ? <Button variant="text" size="small" onClick={clear}>Clear filters</Button> : null}
      </Box>
    </Box>
  );
}

function sydneyToday(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

function dateError(key: DateKey, drafts: Partial<Record<DateKey, string>>, filters: LensFilters): string | null {
  const draft = drafts[key];
  return draft === undefined ? null : dateProblem(key, draft, filters, sydneyToday());
}

function DateField({ label, value, error = null, disabled = false, onChange }: { label: string; value: string; error?: string | null; disabled?: boolean; onChange: (value: string) => void }) {
  return (
    <TextField
      size="small"
      type="date"
      label={label}
      value={value}
      disabled={disabled}
      error={Boolean(error)}
      helperText={error ?? undefined}
      inputProps={{ max: sydneyToday() }}
      onChange={(event) => onChange(event.target.value)}
      InputLabelProps={{ shrink: true }}
      sx={{ maxWidth: 220 }}
    />
  );
}

function Choice({ label, value, options, onChange }: { label: string; value: string; options: ReadonlyArray<string>; onChange: (value: string) => void }) {
  return (
    <TextField select size="small" label={label} value={value} onChange={(event) => onChange(event.target.value)} sx={{ minWidth: 180 }}>
      {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
    </TextField>
  );
}

function ViewLabel({ name }: { name: string }) {
  const [left, right] = name.split(' | ');
  return <span><span>{left}</span><span>{' | '}</span><span>{right}</span></span>;
}

function ExportButton({ fileName, csvText }: { fileName: string; csvText: string }) {
  return <Button variant="outlined" onClick={() => download(fileName, csvText)}>Export CSV</Button>;
}

/** Tables show the first rows on screen; the CSV always has every row. */
const TABLE_ROW_LIMIT = 80;

function TableNote({ shown, total, order }: { shown: number; total: number; order: string }) {
  if (total <= shown) return null;
  return (
    <Typography variant="caption" color="text.secondary">
      Showing the first {shown.toLocaleString('en-AU')} of {total.toLocaleString('en-AU')} rows, {order}. Export CSV for every row.
    </Typography>
  );
}

function EmptyTable({ onReset }: { onReset: () => void }) {
  return (
    <Alert severity="info" action={<Button color="inherit" size="small" onClick={onReset}>Reset</Button>}>
      No rows for these filters.
    </Alert>
  );
}

function ConversionView({ model, csvText, both, fileName, onReset }: { model: ReturnType<typeof conversionModel>; csvText: string; both: boolean; fileName: string; onReset: () => void }) {
  const kpis = conversionKpis(model.totals);
  const currentRows = model.table.filter((row) => row.period === 'Current Period');
  const previousRows = model.table.filter((row) => row.period === 'Previous Period');
  const shown = both ? [...currentRows.slice(0, 40), ...previousRows.slice(0, 20)] : currentRows.slice(0, TABLE_ROW_LIMIT);
  const tiles = [
    ['Total Package Option Impressions', formatInteger(kpis.impressions.current), formatSignedPercent(kpis.impressions.change)],
    ['Packages', formatInteger(kpis.packages.current), formatSignedPercent(kpis.packages.change)],
    ['CR (Packages/Page Views)', formatPercent(kpis.cr.current), formatSignedPercent(kpis.cr.change)],
    ['MpPOI', formatMoney(kpis.mppoi.current ?? 0, 2), formatSignedPercent(kpis.mppoi.change)],
    ['GM (BR)', formatMoney(kpis.margin.current, 0), formatSignedPercent(kpis.margin.change)],
    ['TTV (BR)', formatMoney(kpis.ttv.current, 0), formatSignedPercent(kpis.ttv.change)],
  ];
  return (
    <Section title="SPP | Conversion" action={<ExportButton fileName={fileName} csvText={csvText} />}>
      <TileRow tiles={tiles} />
      <LineChart title="Package option impressions by day" points={conversionDays(model.days, 'Current Period')} />
      {currentRows.length === 0 ? <EmptyTable onReset={onReset} /> : null}
      <TableNote shown={both ? Math.min(currentRows.length, 40) : Math.min(currentRows.length, TABLE_ROW_LIMIT)} total={currentRows.length} order="ordered by package option impressions" />
      <DataTable
        headers={[...(both ? ['Period'] : []), 'Property / split', 'Product', 'Package Option Impressions', 'Packages', 'Impression CR', 'Page View CR', 'MpPOI', 'GM (BR)', 'TTV (BR)']}
        rows={shown.map((row) => [
          ...(both ? [row.period] : []),
          row.split1, row.split2, formatInteger(row.impressions), formatInteger(row.packages),
          formatPercent(ratio(row.packages, row.impressions)), formatPercent(ratio(row.packages, row.pageViews)),
          formatMoney(ratio(row.margin, row.impressions) ?? 0, 2), formatMoney(row.margin, 0), formatMoney(row.ttv, 0),
        ])}
      />
    </Section>
  );
}

function SupplierView({ model, csvText, fileName, onReset }: { model: ReturnType<typeof supplierModel>; csvText: string; fileName: string; onReset: () => void }) {
  const currentRows = model.table.filter((row) => row.period === 'Current Period');
  const tiles = [
    ['LPC Impression Share', formatPercent(model.shares.lpc), 'All history'],
    ['LPC Lead Rate', formatPercent(model.shares.lpcLead), 'Lead room'],
    ['LPP Impression Share', formatPercent(model.shares.lpp), 'All history'],
    ['LPP Lead Rate', formatPercent(model.shares.lppLead), 'Lead room'],
  ];
  return (
    <Section title="SPP | Supplier Breakdown" action={<ExportButton fileName={fileName} csvText={csvText} />}>
      <TileRow tiles={tiles} />
      <LineChart title="Impressions by day, selected reporting period" points={model.days} />
      {currentRows.length === 0 ? <EmptyTable onReset={onReset} /> : null}
      <TableNote shown={Math.min(currentRows.length, TABLE_ROW_LIMIT)} total={currentRows.length} order="ordered by impressions" />
      <DataTable
        headers={['Supplier / split', 'Second split', 'Impressions', 'GM (BR)', 'TTV (BR)', 'Items']}
        rows={currentRows.slice(0, TABLE_ROW_LIMIT).map((row) => [
          row.split1, row.split2, formatInteger(row.impressions), formatMoney(row.margin, 0), formatMoney(row.ttv, 0), formatInteger(row.items),
        ])}
      />
    </Section>
  );
}

function LpcView({ model, csvText, fileName, onReset }: { model: ReturnType<typeof lpcModel>; csvText: string; fileName: string; onReset: () => void }) {
  const currentRows = model.table.filter((row) => row.period === 'Current Period');
  const kpi = model.current;
  const prior = model.previous;
  const tiles = [
    ['LPC Rate Impressions', formatInteger(kpi.impressions), formatSignedPercent(ratio(kpi.impressions, prior.impressions) === null ? null : kpi.impressions / prior.impressions - 1)],
    ['Page Views', formatInteger(kpi.pageViews), formatSignedPercent(prior.pageViews ? kpi.pageViews / prior.pageViews - 1 : null)],
    ['Packages', formatInteger(kpi.packages), formatSignedPercent(prior.packages ? kpi.packages / prior.packages - 1 : null)],
    ['CR (Packages/Page Views)', formatPercent(kpi.cr), ''],
    ['LPC Impression Share', formatPercent(kpi.share, 1), ''],
    ['CR (Packages/Impressions)', formatPercent(kpi.impressionCr), ''],
    ['GM AR', formatMoney(kpi.margin, 0), formatSignedPercent(prior.margin ? kpi.margin / prior.margin - 1 : null)],
  ];
  return (
    <Section title="LPC | Performance by Campaign" action={<ExportButton fileName={fileName} csvText={csvText} />}>
      <TileRow tiles={tiles} />
      <LineChart title="LPC rate impressions by day" points={model.days} />
      {currentRows.length === 0 ? <EmptyTable onReset={onReset} /> : null}
      <TableNote shown={Math.min(currentRows.length, TABLE_ROW_LIMIT)} total={currentRows.length} order="ordered by page views" />
      <DataTable
        headers={['Vendor / split', 'Campaign split', 'Page Views', 'LPC Rate Impressions', 'Share', 'Packages', 'GM AR']}
        rows={currentRows.slice(0, TABLE_ROW_LIMIT).map((row) => [
          row.split1, row.split2, formatInteger(row.pageViews), formatInteger(row.impressions),
          formatPercent(ratio(row.impressions, row.impressions + row.lppImpressions), 1), formatInteger(row.packages), formatMoney(row.margin, 0),
        ])}
      />
    </Section>
  );
}

function TacticalView({
  model,
  csvText,
  fileName,
  onReset,
}: {
  model: ReturnType<typeof tacticalRows>;
  csvText: string;
  fileName: string;
  onReset: () => void;
}) {
  const totals = tacticalTotals(model.kpis);
  const firstMeasure = tacticalColumns().findIndex(({ format }) => format !== 'text');
  const tiles = [
    ['TTV During', formatMoney(totals.ttvDuring, 0), ''],
    ['TTV Prior', formatMoney(totals.ttvPrior, 0), ''],
    ['Items During', formatInteger(totals.itemsDuring), ''],
    ['Items Prior', formatInteger(totals.itemsPrior), ''],
    ['Margin During', formatMoney(totals.marginDuring, 0), ''],
    ['Margin Prior', formatMoney(totals.marginPrior, 0), ''],
  ];
  return (
    <Section title="LPC | Tactical Campaigns Detail" action={<ExportButton fileName={fileName} csvText={csvText} />}>
      <Typography variant="body2" color="text.secondary">
        During and prior metrics use each campaign’s own windows. Date filters select campaigns by the dates
        shown below.
      </Typography>
      <TileRow tiles={tiles} />
      {model.detail.length === 0 ? <EmptyTable onReset={onReset} /> : null}
      <TableNote
        shown={model.detail.length}
        total={model.detail.length}
        order="ordered by vendor and campaign; scroll across for prior-period and conversion measures"
      />
      <DataTable
        wide
        headers={tacticalColumns().map(({ label }) => label)}
        total={tacticalDisplayRow(tacticalGrandTotal(model.detail))}
        rows={model.detail.map(tacticalDisplayRow)}
        cellStyle={(rowIndex, cellIndex) =>
          cellIndex >= firstMeasure ? tacticalMarginColour(model.detail[rowIndex]) : undefined
        }
      />
    </Section>
  );
}

function Section({ title, action, children }: { title: string; action: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 16, fontWeight: 600 }}>{title}</Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function TileRow({ tiles }: { tiles: string[][] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1 }}>
      {tiles.map(([label, value, note]) => (
        <Box key={label} sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h6" sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 20, fontWeight: 600 }}>{value || '–'}</Typography>
          {note ? <Typography variant="caption" color="text.secondary">{note}</Typography> : null}
        </Box>
      ))}
    </Box>
  );
}

function LineChart({ title, points }: { title: string; points: DayPoint[] }) {
  const theme = useTheme();
  const colour = theme.palette.mode === 'dark' ? '#4EFEB9' : '#0F464F';
  return (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5, minWidth: 0 }}>
      <Typography variant="body2">{title}</Typography>
      {points.length ? <>
        <Box sx={{ height: 260 }} role="group" aria-label={`${title}. Daily impressions, ${points[0].day} to ${points[points.length - 1].day}.`}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart data={points} margin={{ top: 24, right: 16, bottom: 28, left: 12 }} accessibilityLayer aria-label={title}>
              <CartesianGrid stroke={theme.palette.divider} vertical={false} />
              <XAxis dataKey="day" tickFormatter={(day: string) => day.slice(5)} minTickGap={36} tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontFamily: FONT_STACK_SUISSE_INTL }} label={{ value: 'Date (month-day)', position: 'insideBottom', offset: -20, fill: theme.palette.text.secondary, fontFamily: FONT_STACK_SUISSE_INTL }} />
              <YAxis width={80} domain={[0, 'auto']} tickFormatter={formatInteger} tick={{ fill: theme.palette.text.secondary, fontSize: 12, fontFamily: FONT_STACK_SUISSE_INTL }} label={{ value: 'Impressions', position: 'top', fill: theme.palette.text.secondary, fontSize: 12, fontFamily: FONT_STACK_SUISSE_INTL }} />
              <Tooltip labelFormatter={(day) => String(day)} formatter={(value) => [formatInteger(Number(value)), 'Impressions']} contentStyle={{ backgroundColor: theme.palette.background.paper, borderColor: theme.palette.divider, color: theme.palette.text.primary, fontFamily: FONT_STACK_SUISSE_INTL, fontSize: 13 }} />
              <Line type="linear" dataKey="value" name="Impressions" stroke={colour} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
            </RechartsLineChart>
          </ResponsiveContainer>
        </Box>
        <Typography variant="caption" color="text.secondary">Source: accommodation report through Lens · {points[0].day} – {points[points.length - 1].day}. Focus the chart and use the arrow keys, or point to the line, for daily values.</Typography>
      </> : <Typography variant="body2" color="text.secondary">No daily impressions for this period.</Typography>}
    </Box>
  );
}

const PRINT_HEADER_LINE = 10;
const PRINT_CELL_LINE = 20;

function printLines(text: string, limit: number): React.ReactNode {
  const lines: Array<string> = [];
  for (const word of text.split(' ')) {
    if (lines.length && `${lines[lines.length - 1]} ${word}`.length <= limit) lines[lines.length - 1] += ` ${word}`;
    else lines.push(word);
  }
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {index ? <br /> : null}
      {line}
    </React.Fragment>
  ));
}

function DataTable({
  headers,
  rows,
  wide = false,
  total,
  cellStyle,
}: {
  headers: Array<string>;
  rows: Array<Array<string>>;
  wide?: boolean;
  total?: Array<string>;
  cellStyle?: (rowIndex: number, cellIndex: number) => React.CSSProperties | undefined;
}) {
  const printing = useNativePrintMode() || Boolean(nativePrintSession());
  if (!rows.length) return null;
  return (
    <Box
      sx={{
        overflow: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 1,
        minWidth: 0,
        ...(wide ? (printing ? { overflow: 'visible' } : { maxHeight: '70vh' }) : {}),
      }}
    >
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            textAlign: 'left',
            p: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontFamily: FONT_STACK_SUISSE_INTL,
            fontSize: 13,
            whiteSpace: 'nowrap',
          },
          ...(wide
            ? {
                '& thead th': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.paper',
                  whiteSpace: 'normal',
                  minWidth: 100,
                  verticalAlign: 'bottom',
                },
                '& th:first-of-type, & td:first-of-type': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  minWidth: 160,
                  maxWidth: 220,
                  whiteSpace: 'normal',
                },
                '& th:first-of-type': { zIndex: 3 },
                '& td:nth-of-type(3)': { minWidth: 220, maxWidth: 300, whiteSpace: 'normal' },
                '& th:nth-of-type(n+11), & td:nth-of-type(n+11)': {
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                },
                '& tbody tr[data-total] td': { fontWeight: 600 },
                ...(printing
                  ? {
                      '& th, & td': { px: 0.5, py: 0.5, fontSize: 11 },
                      '& thead th': { verticalAlign: 'bottom', whiteSpace: 'normal' },
                    }
                  : {}),
              }
            : {}),
        }}
      >
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{printing && wide ? printLines(header, PRINT_HEADER_LINE) : header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {total ? (
            <tr data-total="">
              {total.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ) : null}
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} style={cellStyle?.(index, cellIndex)}>
                  {printing && wide ? printLines(cell, PRINT_CELL_LINE) : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

export default function AccommodationViewsImpressionsSalesPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="accommodation-views-impressions-sales"
      title="Accommodation Views, Impressions and Sales"
      printTarget={printTarget}
      analytics
    >
      <Content />
    </NativeReportShell>
  );
}
